import React from 'react';
import { useShellAppContext } from 'features/shell/context/useShellAppContext';

/**
 * PageAccessGate — capability gate for shell-owned pages.
 *
 * Renders `children` when any required capability is present.
 * Otherwise renders `fallback` (defaults to null).
 *
 * Responsibility scope:
 *   This gate is a capability access control only.  Shell-session presence
 *   and bootstrap recovery are the responsibility of shell-level boundaries:
 *     - ShellSessionGate (missing/expired session at mount)
 *     - ShellAppContextProvider onAuthFailure (mid-session auth expiry)
 *     - ShellBootstrapEntry (bootstrap with returnTo handling)
 *   PageAccessGate does NOT duplicate that responsibility.
 *
 * States:
 *   capability-disabled — requiresCapability is set and the capability is absent.
 *                         Renders `fallback` or null.
 *   allowed             — no capability required, or required capability present.
 *                         Renders children.
 *
 * Note: when `gatewayClient` is absent (null session in standalone/fail-open
 * mode), `capabilities` defaults to `{}`.  Required capabilities will therefore
 * not be present and the gate will block gracefully.  Pages without
 * `requiresCapability` pass through regardless — which is intentional: shell
 * gates own session recovery, not page-level gates.
 *
 * @param {{
 *   children: React.ReactNode,
 *   requiresCapability?: string|null,
 *   fallback?: React.ReactNode,
 * }} props
 */
export function PageAccessGate({ children, requiresCapability = null, fallback = null }) {
  const { capabilities } = useShellAppContext();

  if (requiresCapability !== null && !capabilities?.[requiresCapability]) {
    return fallback !== null ? <>{fallback}</> : null;
  }

  return <>{children}</>;
}
